/**
 * BetaToolsCard — admin tool surface for the beta-launch unblock pipeline.
 *
 * Three operations, all hitting endpoints that ship in this final deploy:
 *
 *   1. Send test verification email → /api/admin/send-test-verification
 *      Returns structured outcome (generated/sent/failed) + hash prefix
 *      that correlates with the audit events.
 *
 *   2. View verification audit events → /api/admin/verification-events
 *      Filter by email, see the full register → token_inserted →
 *      verification_email_sent → verify_success chain.
 *
 *   3. Reminder pipeline health → /api/admin/reminder-system-status
 *      Live counts of 24h sent/failed nudges + last error.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShieldCheck, Send, History, Bell, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_COLOR = {
  register: 'bg-blue-50 text-blue-800',
  beta_mode_auto_verified: 'bg-violet-50 text-violet-800',
  token_issue_start: 'bg-amber-50 text-amber-800',
  token_inserted: 'bg-emerald-50 text-emerald-800',
  verification_email_sent: 'bg-emerald-50 text-emerald-800',
  verification_email_failed: 'bg-rose-50 text-rose-800',
  verify_attempted: 'bg-gray-50 text-gray-700',
  verify_success: 'bg-emerald-50 text-emerald-800',
  verify_failure: 'bg-rose-50 text-rose-800',
  resend_requested: 'bg-amber-50 text-amber-800',
};

export const BetaToolsCard = () => {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [events, setEvents] = useState(null);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [reminder, setReminder] = useState(null);
  const [loadingReminder, setLoadingReminder] = useState(false);

  const doSendTest = async () => {
    if (!email.trim()) { toast.error('Enter an email first'); return; }
    setSending(true);
    setSendResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/send-test-verification`,
        { email: email.trim(), name: 'HealthLoop QA' },
        { withCredentials: true },
      );
      setSendResult(data);
      if (data?.user_not_found) toast.warning('No user with that email — nothing sent.');
      else if (data?.ok) toast.success('Verification email sent.');
      else toast.error(`Send failed: ${data?.failure_reason || 'unknown'}`);
      // Auto-refresh events
      void loadEvents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const loadEvents = async () => {
    if (!email.trim()) { toast.error('Enter an email first'); return; }
    setLoadingEvents(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/verification-events?email=${encodeURIComponent(email.trim())}&limit=50`,
        { withCredentials: true },
      );
      setEvents(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Event load failed');
    } finally {
      setLoadingEvents(false);
    }
  };

  const loadReminder = async () => {
    setLoadingReminder(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/reminder-system-status`,
        { withCredentials: true },
      );
      setReminder(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reminder status load failed');
    } finally {
      setLoadingReminder(false);
    }
  };

  return (
    <Card
      className="border border-violet-200 bg-violet-50/30 rounded-2xl shadow-sm"
      data-testid="beta-tools-card"
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-violet-900">
          <ShieldCheck className="h-5 w-5" />
          Beta Launch Tools
        </CardTitle>
        <CardDescription className="text-violet-800">
          Email-verification audit trail, test sends, and reminder pipeline health.
          Built for the beta-launch round of stabilization.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* SHARED INPUT */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            data-testid="beta-tools-email"
            type="email"
            placeholder="qa-tester@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-white"
          />
          <Button
            data-testid="beta-tools-send-test-btn"
            onClick={doSendTest}
            disabled={sending}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Send className="h-4 w-4 mr-1" />
            {sending ? 'Sending…' : 'Send test verification'}
          </Button>
          <Button
            data-testid="beta-tools-events-btn"
            onClick={loadEvents}
            disabled={loadingEvents}
            variant="outline"
          >
            <History className="h-4 w-4 mr-1" />
            {loadingEvents ? 'Loading…' : 'View audit chain'}
          </Button>
        </div>

        {/* SEND RESULT */}
        {sendResult && (
          <div className={`rounded-md border p-3 text-sm ${sendResult.ok ? 'border-emerald-200 bg-white' : 'border-rose-200 bg-rose-50/50'}`}>
            <div className="font-medium mb-1 flex items-center gap-1">
              {sendResult.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertCircle className="h-4 w-4 text-rose-600" />}
              Send outcome
            </div>
            <ul className="text-xs font-mono space-y-0.5">
              <li>email: {sendResult.email}</li>
              <li>token_generated: <span className={sendResult.token_generated ? 'text-emerald-700' : 'text-rose-700'}>{String(Boolean(sendResult.token_generated))}</span></li>
              <li>email_sent: <span className={sendResult.email_sent ? 'text-emerald-700' : 'text-rose-700'}>{String(Boolean(sendResult.email_sent))}</span></li>
              {sendResult.token_hash_prefix && <li>token_hash_prefix: {sendResult.token_hash_prefix}</li>}
              {sendResult.failure_reason && <li className="text-rose-700">failure: {sendResult.failure_reason}</li>}
              {sendResult.user_not_found && <li className="text-amber-700">user_not_found: true</li>}
            </ul>
          </div>
        )}

        {/* EVENTS LIST */}
        {events && (
          <div className="rounded-md border border-violet-200 bg-white p-3 text-sm">
            <div className="font-medium text-gray-900 mb-2">
              Audit chain ({events.total} events for {events.filter_email})
            </div>
            {events.items.length === 0 ? (
              <div className="text-xs text-rose-700">
                No events — the user has not interacted with the verification pipeline (or was created before this audit was deployed).
              </div>
            ) : (
              <ul className="space-y-1 text-xs font-mono">
                {events.items.map((e, i) => (
                  <li
                    key={i}
                    className={`rounded px-2 py-1 ${KIND_COLOR[e.kind] || 'bg-gray-50 text-gray-700'}`}
                  >
                    <span className="text-gray-500">{e.ts.slice(0, 19)}</span>
                    {' · '}
                    <b>{e.kind}</b>
                    {e.token_hash_prefix ? ` · hash=${e.token_hash_prefix}` : ''}
                    {e.detail ? ` · ${e.detail}` : ''}
                    {' · '}
                    <span className={e.success ? 'text-emerald-700' : 'text-rose-700'}>
                      {e.success ? 'ok' : 'failed'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* REMINDER STATUS */}
        <div className="pt-3 border-t border-violet-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-violet-900 flex items-center gap-1">
              <Bell className="h-4 w-4" />
              Reminder pipeline status
            </div>
            <Button
              data-testid="beta-tools-reminder-btn"
              onClick={loadReminder}
              disabled={loadingReminder}
              size="sm"
              variant="ghost"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              {loadingReminder ? 'Loading…' : 'Refresh'}
            </Button>
          </div>
          {reminder && (
            <ul className="text-xs font-mono space-y-0.5 bg-white border border-violet-200 rounded p-2">
              <li>nudge_emails_enabled: <span className={reminder.nudge_emails_enabled ? 'text-emerald-700' : 'text-rose-700'}>{String(reminder.nudge_emails_enabled)}</span></li>
              <li>sent_24h: <span className="text-emerald-700">{reminder.sent_24h ?? 'n/a'}</span></li>
              <li>failed_24h: <span className={reminder.failed_24h ? 'text-rose-700' : 'text-gray-700'}>{reminder.failed_24h ?? 'n/a'}</span></li>
              {reminder.last_tick && <li>last_tick.ts: {reminder.last_tick.ts}</li>}
              {reminder.last_error && (
                <li className="text-rose-700">
                  last_error.ts: {reminder.last_error.ts} · {reminder.last_error.error}
                </li>
              )}
            </ul>
          )}
        </div>

        <p className="text-[11px] text-violet-800">
          <b>BETA_MODE</b> is currently controlled by the <code>BETA_MODE</code> env var
          in your secrets panel. When <code>true</code>, every new registration is auto-verified.
          Toggle to <code>false</code> after public launch.
        </p>
      </CardContent>
    </Card>
  );
};

export default BetaToolsCard;
